import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { SEO } from "../components/SEO";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, removeItem, updateQty, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal >= 999 || subtotal === 0 ? 0 : 79;

  return (
    <div data-testid="cart-page">
      <SEO title="Your Cart" path="/cart" noindex />
      <section className="pt-16 lg:pt-24 pb-8 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Your Cart</p>
          <h1 className="font-editorial text-6xl md:text-8xl leading-[0.9] tracking-tighter text-ink text-balance">
            The <em className="text-crimson not-italic">shelf.</em>
          </h1>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          {items.length === 0 ? (
            <div className="py-24 text-center border border-crimson/15" data-testid="cart-empty">
              <ShoppingBag strokeWidth={1} className="w-14 h-14 mx-auto text-crimson mb-6" />
              <p className="font-editorial italic text-3xl text-ink-muted mb-6">Your cart is empty.</p>
              <Link
                to="/books"
                data-testid="cart-empty-cta"
                className="inline-flex items-center gap-3 bg-crimson text-ivory px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500"
              >
                Browse the Catalogue →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-10">
              <div className="col-span-12 lg:col-span-8">
                <div className="border-y border-crimson/15">
                  {items.map((item) => (
                    <div key={item.slug} className="grid grid-cols-12 items-center gap-4 py-6 border-b border-crimson/15 last:border-0" data-testid={`cart-item-${item.slug}`}>
                      <Link to={`/books/${item.slug}`} className="col-span-3 md:col-span-2">
                        <img src={item.cover} alt={item.title} className="w-full aspect-[2/3] object-contain bg-ivory-soft" />
                      </Link>
                      <div className="col-span-9 md:col-span-5">
                        <Link to={`/books/${item.slug}`} className="font-editorial text-xl md:text-2xl text-ink hover:text-crimson">
                          {item.title}
                        </Link>
                        <p className="text-sm text-ink-muted mt-1">₹{item.price}</p>
                      </div>
                      <div className="col-span-6 md:col-span-3 flex items-center gap-3">
                        <div className="inline-flex border border-crimson/30">
                          <button onClick={() => updateQty(item.slug, item.qty - 1)} className="px-3 py-2 hover:bg-crimson/5" data-testid={`cart-qty-dec-${item.slug}`} aria-label="Decrease">
                            <Minus strokeWidth={1.5} className="w-3 h-3" />
                          </button>
                          <span className="px-4 py-2 min-w-[3rem] text-center border-x border-crimson/30 text-sm" data-testid={`cart-qty-${item.slug}`}>{item.qty}</span>
                          <button onClick={() => updateQty(item.slug, item.qty + 1)} className="px-3 py-2 hover:bg-crimson/5" data-testid={`cart-qty-inc-${item.slug}`} aria-label="Increase">
                            <Plus strokeWidth={1.5} className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div className="col-span-6 md:col-span-2 flex items-center justify-between md:justify-end gap-4">
                        <p className="font-editorial text-xl text-ink">₹{item.price * item.qty}</p>
                        <button onClick={() => removeItem(item.slug)} className="text-ink-muted hover:text-crimson" data-testid={`cart-remove-${item.slug}`} aria-label="Remove">
                          <X strokeWidth={1.5} className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={clearCart} className="mt-6 text-xs uppercase tracking-[0.24em] text-ink-muted hover:text-crimson link-underline" data-testid="cart-clear">
                  Clear cart
                </button>
              </div>

              <aside className="col-span-12 lg:col-span-4">
                <div className="border border-crimson/20 bg-ivory-soft p-8" data-testid="cart-summary">
                  <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">Order Summary</p>
                  <div className="space-y-3 text-ink-soft">
                    <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal}</span></div>
                    <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-crimson/15 flex justify-between font-editorial text-2xl text-ink">
                    <span>Total</span><span data-testid="cart-total">₹{subtotal + shipping}</span>
                  </div>
                  <button
                    onClick={() => navigate("/checkout")}
                    data-testid="cart-checkout-btn"
                    className="mt-8 w-full bg-crimson text-ivory px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500"
                  >
                    Proceed to Checkout →
                  </button>
                  <Link to="/books" className="mt-4 block text-center text-xs uppercase tracking-[0.24em] text-ink-muted hover:text-crimson link-underline">
                    ← Continue browsing
                  </Link>
                </div>
              </aside>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
